/**
 * attendeeStatus — group an event's attendees by response, provider-neutral.
 *
 * Google and Microsoft Graph spell responses differently. The aggregator
 * normalises the KEY (`response_status`) on every path, but not the VALUES,
 * so the full-event Tracking panel (#680, prototype `fullGroups`) maps both
 * vocabularies onto the prototype's four groups here.
 */

export const RESPONSE_GROUPS = [
    { key: 'accepted', label: 'Accepted', color: '#34D188' },
    { key: 'tentative', label: 'Tentative', color: '#FF973C' },
    { key: 'declined', label: 'Declined', color: '#F5354E' },
    { key: 'none', label: 'No response', color: '#C9C4BD' },
];

const VALUE_TO_GROUP = {
    // Google
    accepted: 'accepted',
    tentative: 'tentative',
    declined: 'declined',
    needsaction: 'none',
    // Microsoft Graph
    organizer: 'accepted',
    tentativelyaccepted: 'tentative',
    notresponded: 'none',
    none: 'none',
};

export function responseGroupOf(value) {
    return VALUE_TO_GROUP[String(value || '').toLowerCase()] || 'none';
}

/**
 * @param {Array<{email?: string, name?: string, response_status?: string}>} attendees
 * @returns {{ groups: Array<{key,label,color,people}>, hasAnyStatus: boolean, summary: string }}
 *   Empty groups are omitted. `hasAnyStatus` is false when no attendee carried a
 *   response at all (booking sources), so the panel can say so instead of
 *   claiming everyone ignored the invite.
 */
export function groupAttendees(attendees) {
    const list = (attendees || []).filter((a) => a && (a.email || a.name));
    const buckets = Object.fromEntries(RESPONSE_GROUPS.map((g) => [g.key, []]));
    let hasAnyStatus = false;
    for (const a of list) {
        if (a.response_status) hasAnyStatus = true;
        buckets[responseGroupOf(a.response_status)].push(a);
    }
    const groups = RESPONSE_GROUPS.filter((g) => buckets[g.key].length)
        .map((g) => ({ ...g, people: buckets[g.key] }));
    const parts = [];
    if (buckets.accepted.length) parts.push(`${buckets.accepted.length} accepted`);
    if (buckets.tentative.length) parts.push(`${buckets.tentative.length} tentative`);
    if (buckets.declined.length) parts.push(`${buckets.declined.length} declined`);
    if (buckets.none.length) parts.push(`${buckets.none.length} no reply`);
    return { groups, hasAnyStatus, summary: parts.join(' · ') };
}
